import React, { useCallback, useEffect, useState } from 'react';
import SanctionsPageShell from '../components/foundry/SanctionsPageShell';
import FoundryCard from '../components/foundry/FoundryCard';
import FoundryButton from '../components/foundry/FoundryButton';
import FoundryBadge from '../components/foundry/FoundryBadge';
import FoundryFilterSelect from '../components/foundry/FoundryFilterSelect';
import CountryRuleEditor from '../components/foundry/CountryRuleEditor';
import { CountryRule as Rule, CountryRuleChanges, CountryRuleOptions } from '../components/foundry/countryRuleTypes';
type Props = {
  apiFetch: (path: string, init?: RequestInit) => Promise<Response>;
};
const control = 'mt-2 w-full border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 disabled:opacity-50 dark:border-white/20 dark:bg-slate-950 dark:text-white';
const HsCountriesPage: React.FC<Props> = ({
  apiFetch
}) => {
  const [rows, setRows] = useState<Rule[]>([]);
  const [options, setOptions] = useState<CountryRuleOptions>({
    colors: [],
    ports: [],
    modalities: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [editing, setEditing] = useState<{
    original: Rule | null;
    draft: CountryRuleChanges;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [colors, setColors] = useState<string[]>([]);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const responses = await Promise.all([apiFetch('/api/admin/hs-countries'), apiFetch('/api/admin/hs-countries/options')]);
      const [list, choices] = await Promise.all(responses.map(r => r.json()));
      if (responses.some(r => !r.ok)) throw Error(list.error || choices.error || 'Landregels laden is mislukt.');
      setRows(list.items);
      setOptions(choices);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Landregels laden is mislukt.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);
  useEffect(() => {
    void load();
  }, [load]);
  const save = async (changes: CountryRuleChanges) => {
    if (!editing) return;
    setSaving(true);
    setSaveError('');
    const ruleId = editing.original?.ruleId;
    try {
      const response = await apiFetch(ruleId ? `/api/admin/hs-countries/${ruleId}` : '/api/admin/hs-countries', {
        method: ruleId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ruleId ? {
          changes,
          original: editing.original
        } : {
          changes
        })
      });
      const payload = await response.json();
      if (!response.ok) throw Error(payload.error || 'Opslaan is mislukt.');
      setRows(current => ruleId ? current.map(row => row.ruleId === ruleId ? payload.item : row) : [...current, payload.item]);
      setOptions(current => ({
        ...current,
        colors: current.colors.map(color => color.color === payload.item.color ? {
          ...color,
          categories: [...new Set([...color.categories, payload.item.category])].sort(),
          actions: [...new Set([...color.actions, payload.item.action])].sort()
        } : color)
      }));
      setEditing(null);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Opslaan is mislukt.');
    } finally {
      setSaving(false);
    }
  };
  const editor = editing && <div id={`landregel-editor-${editing.original?.ruleId ?? 'new'}`} className="p-5 sm:p-6"><h3 className="font-semibold">{editing.original ? `Landregel ${editing.original.ruleId} bewerken` : 'Nieuwe landregel'}</h3><CountryRuleEditor key={editing.original?.ruleId ?? 'new'} initial={editing.draft} options={options} busy={saving} error={saveError} onSubmit={changes => void save(changes)} onCancel={() => setEditing(null)} /></div>;
  const filtered = rows.filter(row => (!colors.length || colors.includes(row.color)) && [row.ruleId, row.country, row.category, row.modelRole, row.destinations.map(item => item.label).join('; '), row.modalities.join('; '), row.action].some(value => String(value ?? '').toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())));
  return <SanctionsPageShell active="countries" canManage>

    <h2 className="it-heading">HS-landen</h2><p className="it-description">Bekijk en wijzig landrisico’s en de bijbehorende categorieën en acties.</p>
    {error && <div role="alert" className="mt-5 border border-red-300 p-4 text-sm text-red-700 dark:text-red-300">{error}</div>}

    <FoundryCard className="mt-5 p-5 sm:p-6">
      <div className="flex flex-wrap items-end gap-5"><label className="min-w-48 flex-1 text-sm font-medium">Zoeken<input value={search} onChange={e => setSearch(e.target.value)} className={control} placeholder="Zoek land, port of actie" /></label><FoundryButton className="h-10 shrink-0" disabled={loading || saving} onClick={() => {
            setEditing({
              original: null,
              draft: {
                color: 'GROEN',
                country: '',
                category: '',
                action: '',
                modelRole: null,
                destinations: [],
                modalities: []
              }
            });
            setSaveError('');
          }}>Nieuwe landregel</FoundryButton><div className="w-full sm:w-60"><p className="mb-2 text-sm font-medium">Kleur</p><FoundryFilterSelect label="Kleur" allLabel="Alle kleuren" options={options.colors.map(option => option.color)} value={colors} onChange={setColors} disabled={loading || saving} /></div><FoundryButton className="h-10 w-28 shrink-0" disabled={loading || saving} onClick={() => void load()}>Verversen</FoundryButton></div>
      {loading ? <p role="status" className="mt-5 text-sm text-slate-500">Landregels laden…</p> : <div className="mt-5 overflow-x-auto"><table className="w-full min-w-[1000px] text-left text-sm"><thead className="border-b border-slate-200 text-xs uppercase text-slate-500 dark:border-white/10"><tr>{['RuleId', 'Kleur', 'Land', 'Functie in risicomodel', 'Ports/hubs/freezones', 'Modaliteiten', 'Actie'].map(label => <th key={label} className="p-3">{label}</th>)}<th className="w-28 pb-3 pl-3 pr-0 pt-3 text-right"><span className="sr-only">Bewerken</span></th></tr></thead><tbody>{editing && !editing.original && <tr className="border-b border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-slate-950/50"><td colSpan={8}>{editor}</td></tr>}{filtered.map(row => <React.Fragment key={row.ruleId}><tr className="border-b border-slate-100 align-top dark:border-white/10"><td className="p-3">{row.ruleId}</td><td className="p-3"><FoundryBadge variant={row.color === 'ROOD' ? 'red' : row.color === 'ORANJE' ? 'orange' : 'green'}>{row.color}</FoundryBadge></td><td className="p-3 font-medium">{row.country}</td><td className="p-3">{row.modelRole || '—'}</td><td className="p-3">{row.destinations.map(item => item.label).join('; ') || 'Alle ports'}</td><td className="p-3">{row.modalities.join('; ') || '—'}</td><td className="p-3">{row.action}</td><td className="pb-3 pl-3 pr-0 pt-3 text-right"><FoundryButton className="h-10 w-28" disabled={saving} aria-label={`${row.country} bewerken`} aria-expanded={editing?.original?.ruleId === row.ruleId} aria-controls={`landregel-editor-${row.ruleId}`} onClick={() => {
                      setEditing(current => current?.original?.ruleId === row.ruleId ? null : {
                        original: row,
                        draft: {
                          ...row
                        }
                      });
                      setSaveError('');
                    }}>{editing?.original?.ruleId === row.ruleId ? 'Sluiten' : 'Bewerken'}</FoundryButton></td></tr>{editing?.original?.ruleId === row.ruleId && <tr className="border-b border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-slate-950/50"><td colSpan={8}>{editor}</td></tr>}</React.Fragment>)}</tbody></table>{filtered.length === 0 && <p className="p-3 text-sm text-slate-500">Geen landregels gevonden.</p>}</div>}
    </FoundryCard>
  </SanctionsPageShell>;
};
export default HsCountriesPage;
